import React, { useState, useEffect } from 'react';
import throttle from 'lodash.throttle';

const RECHECK_TIMEOUT = 300;

const DEFAULT_SCROLL_MARGIN = 75;

const DIRECTION_BOTTOM = 'bottom';
const DIRECTION_TOP = 'top';

/**
 * React hook that keeps track of lazy load state based on window scroll position.
 *
 * @param {String} nodeID                The unique ID of the DOM element to
 *                                       listen for scroll events.
 * @param {Object} options               The lazy load options.
 * @param {String} options.direction     The direction to load, i.e. "top" or "bottom".
 * @param {Number} options.scrollMargin  The px margin to pre-trigger scroll events.
 * @param {Bool} options.listenToWindow  True, if we want to listen to the window scroll events.
 * @param {Number} options.offset        The initial offset position.
 *
 * @returns {Object} loadState          The lazy load state.
 * @returns {Number} loadState.offset   The current offset.
 * @returns {Bool} loadState.loading    True, if the data is loading.
 * @returns {Bool} loadState.loadedAll  True, if all data has loaded.
 */
const useLazyLoad = (nodeID, options = {}) => {

	// Determine the directionality to trigger lazy load events based on scroll
	const direction = options.direction || DIRECTION_BOTTOM;

	// Determine the scroll margin to add a buffer to help pre-trigger scroll events
	const scrollMargin = options.scrollMargin || DEFAULT_SCROLL_MARGIN;

	// Check if we should listen to scroll events on the window vs. on the provided dom node iD.
	const listenToWindow = options.listenToWindow || false;

	const offset = options.offset || 0;

	// Keep track of the lazy load state
	const [loadState, setLoadState] = useState({
		loading: false,
		loadedAll: false,
		offset,
		ready: false,
	});

	useEffect(() => {

		// Don't listen to scroll events until we are ready...
		if (!loadState.ready) {
			// NOTE: If using SSR, make sure that "ready=false"!
			// eslint-disable-next-line react/display-name
			return () => null;
		}

		// Determine the DOM node to contain the lazy loaded content
		const nodeToMeasure = document.getElementById(nodeID);

		// Determine the DOM node or window to listen to for scroll events
		const nodeToListen = listenToWindow ? window : nodeToMeasure;

		const handleOnScroll = throttle(() => {

			// Check if currently triggering a lazy load or are already done loading...
			if (loadState.loading || loadState.loadedAll) {
				return;
			}

			let nearBottom = false;

			let nearTop = false;

			if (listenToWindow) {

				const bottom = nodeToMeasure.getBoundingClientRect().bottom;
				const top = nodeToMeasure.getBoundingClientRect().top;

				nearBottom = (bottom - scrollMargin) <= window.innerHeight;
				nearTop = (top + scrollMargin) >= 0;

			} else {

				const scrollPosition = nodeToMeasure.scrollTop + nodeToMeasure.clientHeight;
				const totalHeight = nodeToMeasure.scrollHeight;

				nearBottom = scrollPosition >= (totalHeight - scrollMargin);
				nearTop = nodeToMeasure.scrollTop < scrollMargin;

			}

			if ((direction === DIRECTION_BOTTOM && nearBottom) ||
				(direction === DIRECTION_TOP && nearTop)) {

				// Trigger a lazy load event and then stop listening for further lazy loads
				setLoadState((prevLoadState) => Object.assign({}, prevLoadState, {
					loading: true,
					ready: false,
				}));

			}

		}, RECHECK_TIMEOUT);

		if (nodeToListen && nodeToMeasure) {
			nodeToListen.addEventListener('scroll', handleOnScroll);
		}

		return () => {

			if (nodeToListen && nodeToMeasure) {
				nodeToListen.removeEventListener('scroll', handleOnScroll);
			}

		};

	}, [loadState, listenToWindow, direction, scrollMargin, nodeID]);

	return [loadState, setLoadState];

};

export default useLazyLoad;
